"use strict"

import CkRedirect from "../../model/domain/ck-redirect"
import Validation from "../../model/util/validation"
import ClassCodeService from "../../services/class-code/class-code.service"
import BreadcrumbService from "../../services/breadcrumb-service/breadcrumb.service"
import AuthService from "../../services/auth/auth.service"
import ToolbarService from "../../services/toolbar/toolbar.service"
import CacheService from "../../services/cache/cache.service"
import RosterService from "../../services/roster/roster.service"
import StorageService from "../../services/storage/storage.service"
import IsteService from "../../services/iste/iste.service"

export class LoginSources {
  static get LOGIN() {
    return "Login"
  }

  static get SIGN_UP() {
    return "Sign up"
  }
}

export default class IsteLoginController {
  /**
   * @param {ClassCodeService} ClassCodeService
   * @param {BreadcrumbService} BreadcrumbService
   * @param {AuthService} AuthService
   * @param {ToolbarService} ToolbarService
   * @param {CacheService} CacheService
   * @param {RosterService} RosterService
   * @param {StorageService} StorageService
   * @param {IsteService} IsteService
   */
  constructor(
    $q,
    $log,
    $timeout,
    $state,
    $stateParams,
    $scope,
    $mdDialog,
    $document,
    $window,
    ClassCodeService,
    BreadcrumbService,
    AuthService,
    ToolbarService,
    CacheService,
    RosterService,
    StorageService,
    IsteService,
  ) {
    "ngInject"

    this.$q = $q
    this.$log = $log
    this.$timeout = $timeout
    this.$state = $state
    this.$stateParams = $stateParams
    this.$scope = $scope
    this.$mdDialog = $mdDialog
    this.$document = $document
    this.$window = $window

    /** @type {ClassCodeService} */
    this._classCodeService = ClassCodeService
    /** @type {BreadcrumbService} */
    this._breadcrumbService = BreadcrumbService
    /** @type {AuthService} */
    this._authService = AuthService
    /** @type {ToolbarService} */
    this._toolbarService = ToolbarService
    /** @type {CacheService} */
    this._cacheService = CacheService
    /** @type {RosterService} */
    this._rosterService = RosterService
    /** @type {StorageService} */
    this._storageService = StorageService
    /** @type {IsteService} */
    this._isteService = IsteService

    this.emailRegex = Validation.EmailRegex

    this._isLoading = false
    this._nameTimeout = null
    this._focusClassCode = true
    this._focusName = false
    this._errorMessage = ""

    this._allowNewMembers = false
    this._classCode = $stateParams.classCode
    this._checkingClassCode = ""
    this._name = $stateParams.name

    this._classCodePattern = "^[a-zA-Z\\d\\s]+$"
    this._classCodeRegex = new RegExp(this._classCodePattern)

    this._classCodeIsValid = false
    this._studentNames = new Set()
    this._nameIsValid = false
    this._isCleverClassCode = false
    this._showClassCodeInput = true

    $timeout(() => this.classCodeUpdated())
  }

  $onInit() {
    if (!this.$stateParams.classCode) {
      this.redirect()
    }
  }

  /**
   * @return {boolean}
   */
  get isLoading() {
    return this._isLoading
  }

  /**
   * @return {boolean}
   */
  get focusClassCode() {
    return this._focusClassCode
  }

  /**
   * @param value {boolean}
   */
  set focusClassCode(value) {
    this._focusClassCode = value
  }

  /**
   * @return {boolean}
   */
  get focusName() {
    return this._focusName
  }

  /**
   * @param value {boolean}
   */
  set focusName(value) {
    this._focusName = value
  }

  /**
   * @return {string}
   */
  get errorMessage() {
    return this._errorMessage
  }

  /**
   * @param value {string}
   */
  set errorMessage(value) {
    this._errorMessage = value
  }

  /**
   * @return {string}
   */
  get anonStudentLoginButtonMessage() {
    return this.canResume ? "Continue" : "Go"
  }

  get showClassCodeInput() {
    return this._showClassCodeInput
  }

  set showClassCodeInput(value) {
    this._showClassCodeInput = value
  }

  // Called whenever the classCode text field is modified
  classCodeUpdated() {
    if (this.$scope.anonStudentLoginForm) {
      this.$scope.anonStudentLoginForm.classCode.$touched = true
      this.$scope.anonStudentLoginForm.name.$touched = true
      this.$scope.anonStudentLoginForm.classCode.$setValidity("bad-ccode", true)
      this.$scope.anonStudentLoginForm.classCode.$setValidity("pattern", true)
    }

    this._studentNames = new Set()
    this.nameUpdated()
    this._classCodeIsValid = false

    let classCodeMaxLength = this._classCode.indexOf(" ") > -1 ? 7 : 6
    this._classCode = this._classCode.toUpperCase().slice(0, classCodeMaxLength)

    if (!this._classCode) {
      return
    } else if (!this._classCodeRegex.test(this._classCode)) {
      if (this.$scope.anonStudentLoginForm) {
        this.$scope.anonStudentLoginForm.classCode.$setValidity(
          "pattern",
          false,
        )
      }
      return
    }

    let cleanedClassCode = this._removeSpaces(this._classCode)

    if (cleanedClassCode.length === 6) {
      if (this._checkingClassCode !== cleanedClassCode) {
        this._isLoading = true
        this._checkingClassCode = cleanedClassCode
        this._loadClassCodeData(cleanedClassCode)
      }
    } else {
      this._checkingClassCode = ""
    }

    var classCode = this.$stateParams.classCode
    if (classCode) {
      this.showClassCodeInput = false
      this._classCodeIsValid = true
    }
  }

  /**
   * @param code {string}
   */
  _removeSpaces(code) {
    return code.replace(/[\s]/g, "")
  }

  /**
   * Verifies that a classCode exists, and then loads the students for that classCode
   * @param tempClassCode
   * @private
   */
  _loadClassCodeData(tempClassCode) {
    this._classCodeService
      .getUsernames(tempClassCode)
      .then((classCodeData) => {
        this._isLoading = false
        if (this._removeSpaces(this._classCode) !== tempClassCode) {
          throw new Error("class Code was modified")
        }

        this.$scope.anonStudentLoginForm.classCode.$setValidity(
          "bad-ccode",
          true,
        )
        this._allowNewMembers = classCodeData.allowNewMembers
        this._classCodeIsValid = true
        this._focusName = true
        this._isPro = classCodeData.pro
        this._hideStudentWork = classCodeData.hideStudentWork

        this._studentNames = new Set(
          classCodeData.names.map((name) => name.toLowerCase()),
        )

        this.nameUpdated()
      })
      .catch((err) => {
        if (this.$scope.anonStudentLoginForm) {
          if (err.message === "clever-import created") {
            this._isCleverClassCode = true
            this.$scope.anonStudentLoginForm.classCode.$setValidity(
              "clever-locked",
              false,
            )
          } else {
            this.$scope.anonStudentLoginForm.classCode.$setValidity(
              "bad-ccode",
              false,
            )
          }
        }
        this.$log.debug("_loadClassCodeData error", err)
        this._isLoading = false
      })
  }

  /**
   * Called whenever the name text field is modified
   */
  nameUpdated() {
    if (this.$scope.anonStudentLoginForm) {
      this.$scope.anonStudentLoginForm.classCode.$touched = true
      this.$scope.anonStudentLoginForm.name.$touched = true
    }

    this.$timeout.cancel(this._nameTimeout)
    this._nameIsValid = false
    this._nameIsPresent = false

    if (this._name) {
      this._nameIsValid = this.isEmailValid(this._name)
      this._nameIsPresent = this._nameIsValid

      if (!this._nameIsPresent) {
        this._nameTimeout = this.$timeout(() => {
          this.$scope.anonStudentLoginForm.name.$setValidity(
            "no-student",
            false,
          )
        }, 4000)
      } else {
        this.$scope.anonStudentLoginForm.name.$setValidity("no-student", true)
      }
    }
  }

  login() {
    if (!this.canStartNew && !this.canResume) {
      return
    }

    this._toolbarService.reset()
    this._cacheService.reset()

    const classCodeWithoutSpaces = this._removeSpaces(this._classCode)
    if (!this._studentNames.has(this._name)) {
      this._isteService.sendIsteDetails(classCodeWithoutSpaces, this._name)
    }

    this.$state.go("root.anon-student-assignment-work", {
      classCode: this._removeSpaces(this._classCode),
      name: this._name,
      source: this.canStartNew ? LoginSources.SIGN_UP : LoginSources.LOGIN,
      question: 1,
    })
  }

  /**
   * no classCode OR
   * classCode not 6 characters OR
   * classCode not valid
   * @returns {boolean}
   */
  get needClassCode() {
    return (
      !this._classCode ||
      this._removeSpaces(this._classCode).length !== 6 ||
      !this._classCodeIsValid
    )
  }

  /**
   * valid classCode AND
   * no name OR
   * name is invalid && disallow new students
   * @returns {boolean}
   */
  get needName() {
    return !this.needClassCode && !this._nameIsValid
  }

  /**
   * valid  class code AND
   * valid name
   * @return {boolean}
   */
  get canStart() {
    return !this.needClassCode && !this.needName
  }

  /**
   * valid classCode AND
   * valid name AND
   * returning user
   * @returns {boolean}
   */
  get canResume() {
    return this.canStart && this._nameIsValid
  }

  /**
   * valid classCode AND
   * some name AND
   * invalid name AND
   * allow new students
   * @returns {boolean}
   */
  get canStartNew() {
    return !this.canResume && !this.needClassCode && !this.needName
  }

  get classCodeMinLength() {
    if (!this._name) {
      return 0
    }
    return 6
  }

  /**
   * @returns {string}
   */
  get name() {
    return this._name
  }

  /**
   * @param value {string}
   */
  set name(value) {
    this._name = value
  }

  /**
   * @returns {string}
   */
  get classCode() {
    return this._classCode
  }

  /**
   * @param value {string}
   */
  set classCode(value) {
    this._classCode = value
  }

  /**
   * @returns {string}
   */
  get classCodePattern() {
    return this._classCodePattern
  }

  goToAccountLogin() {
    if (this.classCodeValid || this._isCleverClassCode) {
      this._storageService.ckRedirect = new CkRedirect("root.account.home", {
        classCode: this._removeSpaces(this.classCode),
      })
    }

    this._breadcrumbService.go("root.account-login", {
      classCode: this._removeSpaces(this._classCode),
    })
  }

  goToCreateAccountChoice() {
    this._breadcrumbService.go("root.signup-choice")
  }

  /**
   * @return {string}
   */
  get whatIsAClassCodeModalId() {
    return "what-is-a-class-code"
  }

  showClassCodeModal() {
    this.$mdDialog.show({
      contentElement: `#${this.whatIsAClassCodeModalId}`,
      parent: angular.element(this.$document[0].body),
      onRemoving: () => {
        this._focusClassCode = true
      },
    })
  }

  get classCodeValid() {
    return this._classCodeIsValid
  }

  get showPro() {
    return (
      this._classCodeIsValid && this._isPro === true && !this._hideStudentWork
    )
  }

  get hideStudentWork() {
    return this._hideStudentWork
  }

  get title() {
    return "Welcome to the ISTE Scavenger Hunt!"
  }

  goToCreateStudentAccount() {
    let classCode = this._removeSpaces(this.classCode)

    this._storageService.ckRedirect = new CkRedirect("root.account.home", {
      classCode,
    })

    this._breadcrumbService.go("root.signup-student", {
      classCode,
    })
  }

  goBack() {
    this.classCode = ""
    this.classCodeUpdated()
    this.showClassCodeInput = true
  }

  isEmailValid(string) {
    return (
      string !== "" && angular.isDefined(string) && this.emailRegex.test(string)
    )
  }

  redirect() {
    this.$window.location.replace("https://classkick.com/iste")
  }
}
